import React, { useEffect, useRef, useState } from "react";
import { Clock4 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

// Course sessions run in the afternoon and evening, with Saturday mornings:
// 07–21 covers every hour a session can start or end without a long scroll.
const HOURS = Array.from({ length: 15 }, (_, i) => String(i + 7).padStart(2, "0"));
const MINUTES = ["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"];

/**
 * "HH:MM", picked the platform's way instead of the browser's: an hour column
 * and a minute column in a popover styled like `DatePickerField`, plus a text
 * box for typing an exact time ("17:40") without scrolling.
 *
 * Picking the hour keeps the popover open for the minutes; picking the minute
 * closes it. The value is committed as soon as both are known.
 */
export default function TimePickerField({
  value, onChange, testid, ariaLabel, placeholder = "--:--", hours = HOURS, minutes = MINUTES, size = "sm",
}) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState(value || "");
  const [hour, minute] = (value || "").split(":");
  const hourRef = useRef(null);

  useEffect(() => { setTyped(value || ""); }, [value, open]);

  // Bring the chosen hour into view when the list opens.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      hourRef.current?.querySelector("[aria-selected='true']")?.scrollIntoView({ block: "center" });
    }, 0);
    return () => clearTimeout(t);
  }, [open]);

  const pickHour = (h) => onChange(`${h}:${minute || "00"}`);
  const pickMinute = (m) => { onChange(`${hour || "18"}:${m}`); setOpen(false); };

  const commitTyped = () => {
    const m = typed.trim().match(/^(\d{1,2})[:.]?(\d{2})$/);
    if (!m) { setTyped(value || ""); return; }
    const h = Number(m[1]);
    const mm = Number(m[2]);
    if (h > 23 || mm > 59) { setTyped(value || ""); return; }
    onChange(`${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`);
    setOpen(false);
  };

  const cell = (on) => `w-full rounded-lg px-2 py-1.5 text-sm font-semibold tabular-nums transition-colors ${
    on ? "bg-[#965EA7] text-white" : "text-[#373A45] hover:bg-[#F0E7F4] hover:text-[#7A4986]"}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          aria-label={ariaLabel}
          className={`group w-full flex items-center justify-between gap-2 bg-white border border-[#E5E7EB] ${size === "md" ? "rounded-xl px-3 py-2.5" : "rounded-lg px-2.5 py-1.5"} text-left text-sm tabular-nums hover:border-[#965EA7]/50 focus:ring-2 focus:ring-[#965EA7] focus:border-transparent outline-none transition-colors ${
            value ? "text-[#373A45]" : "text-[#9CA3AF]"} ${open ? "ring-2 ring-[#965EA7] border-transparent" : ""}`}
        >
          <span>{value || placeholder}</span>
          <Clock4 size={14} className={`shrink-0 ${open ? "text-[#965EA7]" : "text-[#6B7280] group-hover:text-[#965EA7]"}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="z-[70] w-[176px] p-0 rounded-2xl border-[#E5E7EB] shadow-xl overflow-hidden"
        onOpenAutoFocus={(e) => e.preventDefault()}
        data-testid={testid ? `${testid}-popover` : undefined}
      >
        <div className="grid grid-cols-2 border-b border-[#E5E7EB] bg-[#FAFAFB] text-center text-[11px] font-semibold uppercase tracking-wider text-[#9CA3AF]">
          <div className="py-1.5">Ora</div>
          <div className="py-1.5">Minut</div>
        </div>
        <div className="grid grid-cols-2 gap-1 p-1.5">
          <div ref={hourRef} role="listbox" aria-label="Ora" className="max-h-52 overflow-y-auto space-y-0.5 pr-0.5">
            {hours.map((h) => (
              <button key={h} type="button" role="option" aria-selected={h === hour}
                onClick={() => pickHour(h)} className={cell(h === hour)}
                data-testid={testid ? `${testid}-h-${h}` : undefined}>
                {h}
              </button>
            ))}
          </div>
          <div role="listbox" aria-label="Minut" className="max-h-52 overflow-y-auto space-y-0.5 pl-0.5">
            {minutes.map((m) => (
              <button key={m} type="button" role="option" aria-selected={m === minute}
                onClick={() => pickMinute(m)} className={cell(m === minute)}
                data-testid={testid ? `${testid}-m-${m}` : undefined}>
                {m}
              </button>
            ))}
          </div>
        </div>
        <div className="border-t border-[#E5E7EB] bg-[#FAFAFB] p-2">
          <input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onBlur={commitTyped}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitTyped(); } }}
            placeholder="sau scrie, ex. 17:40"
            inputMode="numeric"
            aria-label="Scrie ora exactă"
            className="w-full rounded-lg border border-[#E5E7EB] bg-white px-2 py-1 text-sm tabular-nums text-[#373A45] focus:border-[#965EA7] focus:outline-none"
            data-testid={testid ? `${testid}-typed` : undefined}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
